import React, { useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';

const MyLog = () => {
  const { logs, currentUser, whiteLabel, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key) => getTranslation(key, language);
  
  const [filterType, setFilterType] = useState('All');
  
  // Filter logs for current user
  const sentLogs = logs.filter(log => log.user === currentUser?.name && log.type === 'Deposit');
  const receivedLogs = logs.filter(log => log.recipient === currentUser?.name || 
                                          (log.recipient === currentUser?.email && log.type === 'Deposit'));

  const exportMyCsv = (data, filename) => {
    const headers = [t('csvId'), t('csvTimestamp'), t('csvType'), t('csvLocker'), t('csvFromTo'), t('csvDetails')];
    const rows = data.map(log => [
      log.id,
      log.timestamp.toISOString(),
      log.type,
      log.locker,
      log.type === 'Deposit' ? `${t('to')}: ${log.recipient}` : `${t('from')}: ${log.user}`,
      log.pin ? `${t('pin')}: ${log.pin}` : log.method || t('na')
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('myLog')}</h1>
        <p className="text-gray-600">{t('trackParcels')}</p>
        {currentUser && (
          <p className="text-sm text-gray-500">
            {currentUser.name} ({currentUser.email})
          </p>
        )}
      </div>

      <Tabs defaultValue="sent" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="sent">{t('sent')}</TabsTrigger>
          <TabsTrigger value="received">{t('received')}</TabsTrigger>
        </TabsList>

        <TabsContent value="sent">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>{t('sent')}</CardTitle>
                  <CardDescription>{t('parcelsSent')}</CardDescription>
                </div>
                <Button 
                  onClick={() => exportMyCsv(sentLogs, 'my-sent-parcels')}
                  style={{ backgroundColor: primaryColor }}
                  disabled={sentLogs.length === 0}
                >
                  <Download className="mr-2" size={16} />
                  {t('exportCsv')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {sentLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('noActivity')}</p>
              ) : (
                <div className="space-y-3">
                  {sentLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-semibold text-[#1A1A1A]">{log.locker}</span>
                            <span className="text-xs px-2 py-1 rounded bg-white text-gray-700">{log.size}</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {t('toWhom')}: <span className="font-medium">{log.recipient}</span>
                          </p>
                          {log.pin && (
                            <p className="text-sm text-gray-600">
                              {t('pin')}: <span className="font-mono font-semibold">{log.pin}</span>
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{log.timestamp.toLocaleDateString()}</p>
                          <p className="text-xs text-gray-500">{log.timestamp.toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="received">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>{t('received')}</CardTitle>
                  <CardDescription>{t('parcelsReceived')}</CardDescription>
                </div>
                <Button 
                  onClick={() => exportMyCsv(receivedLogs, 'my-received-parcels')}
                  style={{ backgroundColor: primaryColor }}
                  disabled={receivedLogs.length === 0}
                >
                  <Download className="mr-2" size={16} />
                  {t('exportCsv')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {receivedLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('noActivity')}</p>
              ) : (
                <div className="space-y-3">
                  {receivedLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-semibold text-[#1A1A1A]">{log.locker}</span>
                            {log.size && <span className="text-xs px-2 py-1 rounded bg-white text-gray-700">{log.size}</span>}
                          </div>
                          <p className="text-sm text-gray-600">
                            {t('from')}: <span className="font-medium">{log.user}</span>
                          </p>
                          {log.pin && (
                            <p className="text-sm text-gray-600">
                              {t('pin')}: <span className="font-mono font-semibold">{log.pin}</span>
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{log.timestamp.toLocaleDateString()}</p>
                          <p className="text-xs text-gray-500">{log.timestamp.toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyLog;
